import {connect} from 'pwa-helpers';
import {LitElement, html} from 'lit';
import {navigator, router} from 'lit-element-router';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import deepEqual from 'deep-equal';
import Sortable from 'sortablejs/modular/sortable.complete.esm.js';

import store from '../../store';
import style from '../../../../scss/artworkList.scss';
import listStyle from '../../../../scss/list.scss';
import {
	fetchArtworks,
	selectAllArtwork,
	selectAllMaterials,
	selectAllArtworkTypes,
	selectAllMedia,
	selectArtworks,
	deselectArtworks,
	selectedArtworks,
	selectAllSerie,
	fetchSeries,
	massUpdateArtworks,
} from '../../slices/artworkSlice';
import {getValueForLanguage} from '../../util/getValueForLanguage';
import {selectArtworkQuery, selectProfileDefaultLanguage, selectLanguage} from '../../selectors';
import getRoutes from '../../routes';
import arrowRight from '../../icons/arrowRight';
import grab from '../../icons/grab';
import down from '../../icons/down';
import up from '../../icons/up';
import {isPermitted} from '../../util/isPermitted';

@customElement('arc-artwork-list')
export class ArtworkList extends connect(store)(router(navigator(LitElement))) {
	static styles = [listStyle, style];

	static properties = {
		materials: {type: Array},
		isMobile: {type: Boolean},
		searchText: {type: String},
		filtersVisible: {type: Boolean},
		noFilters: {type: Boolean},
		selectMultiple: {type: Boolean},
		contextClass: {type: String},
		selectedChange: {type: Object},
		deleted: {type: Boolean},
		selected: {type: Boolean},

		manualSortOrder: {type: Array},
		updateNeeded: {type: Boolean},
	};

	constructor() {
		super();
		this.currentLanguage = 'de';
		this.artworks = [];
		this.isMobile = window.innerWidth <= 768;
		this.searchText = '';
		this.filtersVisible = false;
		this.manualSortOrder = [];
		this.seriesId = null;
		this.deleted = false;
		this.selected = false;
		this.updateNeeded = false;
		this.componentActive = true;
		this.selectedChange = {
			publication: '',
			ownership: '',
			series: '',
		};
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.lang = selectLanguage(state);
		this.artworks = selectAllArtwork(state);
		this.materials = selectAllMaterials(state);
		this.artworkTypes = selectAllArtworkTypes(state);
		this.mediaCollections = selectAllMedia(state);
		this.artworkQuery = selectArtworkQuery(state);
		this.totalPages = state.artwork.artworkPagination.pagesAmount;
		this.series = selectAllSerie(state);
		this.sortable = null;
		this.permittedToView = isPermitted(['artwork/view'], this.profileId, state);
		this.permittedToEdit = isPermitted(['artwork/edit'], this.profileId, state);

		this.currentLanguage = selectProfileDefaultLanguage(state);
	}

	update(changedProperties) {
		super.update(changedProperties);

		// fetch new artwork data if needed, but only once <arc-pagination .totalPages=${this.totalPages}></arc-pagination>
		if (this.updateNeeded && this.componentActive) {
			this.fetchArtworks();
		}
	}

	fetchArtworks() {
		this.updateNeeded = false;
		if (this.selected) {
			const ids = selectedArtworks(this.state);
			store.dispatch(
				fetchArtworks({
					profileId: this.profileId,
					artworkIds: '[' + ids.join(',') + ']',
					isDeleted: false,
					...this.query,
					...{withCounters: false},
				})
			);
		} else {
			store.dispatch(
				fetchArtworks({
					profileId: this.profileId,
					seriesId: this.seriesId,
					isDeleted: this.deleted,
					...this.query,
					...{withCounters: true},
				})
			);
		}
	}

	static get routes() {
		return getRoutes();
	}

	async router(route, params, query, data) {
		// do artworks need update from backend
		this.updateNeeded =
			this.route !== route ||
			(route === 'artworks' && (this.profileId !== params.id || !deepEqual(this.query, query))) ||
			(route === 'artworks-bin' &&
				(this.profileId !== params.id || !deepEqual(this.query, query))) ||
			(route === 'series-detail' && this.seriesId !== params.seriesId);

		this.route = route;
		this.params = params;
		this.query = query;
		this.data = data;

		this.profileId = params.id;
		this.seriesId = params.seriesId;
		this.requestUpdate();
	}

	connectedCallback() {
		super.connectedCallback();
		this.addEventListener('artwork-deleted', this._refreshArtworkList.bind(this));
		window.addEventListener('resize', this._handleResize.bind(this));
		if (selectAllSerie(this.state).length === 0) {
			store.dispatch(fetchSeries({profileId: this.profileId}));
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.componentActive = false;
		this.removeEventListener('artwork-deleted', this._refreshArtworkList.bind(this));
		window.removeEventListener('resize', this._handleResize.bind(this));
	}

	firstUpdated(_changedProperties) {
		super.firstUpdated(_changedProperties);
		const sortableElement = this.shadowRoot.querySelector('.list');
		if (this.seriesId && this.sortable === null && sortableElement !== null) {
			this.sortable = Sortable.create(sortableElement, {
				handle: '.dragHandle',
				filter: '.nosort, .listHeader',
				draggable: '.canBeSorted',
				onEnd: (e) => {
					this.sortArtworks(e.oldIndex, e.newIndex);
				},
			});
		}
	}

	_refreshArtworkList() {
		store.dispatch(fetchArtworks({profileId: this.profileId, ...this.query}));
	}

	_handleResize() {
		this.isMobile = window.innerWidth <= 600;
	}

	updateSearch(event) {
		this.searchText = event.target.value.trim().toLowerCase();
	}

	toggleFilters() {
		this.filtersVisible = !this.filtersVisible;
	}

	getOrderQuery(orderBy) {
		return {
			...this.query,
			...{
				orderBy: orderBy,
				orderByDirection: this.artworkQuery.orderByDirection === 'desc' ? 'asc' : 'desc',
			},
		};
	}

	getOrderDirectionIcon(orderBy) {
		if (this.query.orderBy === orderBy) {
			return this.artworkQuery.orderByDirection === 'asc' ? down : up;
		} else {
			return '';
		}
	}

	sortArtworks(oldIndex, newIndex) {
		const storeArtwork = this.artworks[oldIndex - 1];
		this.artworks.splice(oldIndex - 1, 1);
		this.artworks.splice(newIndex - 1, 0, storeArtwork);
		this.requestUpdate();

		const updatedArtworkIds = this.artworks.map((artwork) => artwork.id);

		// dispatch changed order to series-detail, so it saves
		this.dispatchEvent(
			new CustomEvent('artwork-order-changed', {
				detail: {artworkIds: updatedArtworkIds},
				bubbles: true,
				composed: true,
			})
		);
	}

	moveArtwork(index, direction) {
		if (direction === 'up' && index > 0) {
			[this.artworks[index - 1], this.artworks[index]] = [
				this.artworks[index],
				this.artworks[index - 1],
			];
		}

		if (direction === 'down' && index < this.artworks.length - 1) {
			[this.artworks[index + 1], this.artworks[index]] = [
				this.artworks[index],
				this.artworks[index + 1],
			];
		}
		this.requestUpdate();

		const updatedArtworkIds = this.artworks.map((artwork) => artwork.id);
		// dispatch changed order to series-detail, so it saves
		this.dispatchEvent(
			new CustomEvent('artwork-order-changed', {
				detail: {artworkIds: updatedArtworkIds},
				bubbles: true,
				composed: true,
			})
		);
	}

	selectAllArtworks(checked) {
		const artworkIds = this.artworks.map((artwork) => artwork.id);
		if (checked) {
			store.dispatch(selectArtworks({artworkIds: artworkIds}));
		} else {
			store.dispatch(deselectArtworks({artworkIds: artworkIds}));
		}
	}

	onEditArtworks() {
		this.dialog = this.renderRoot.querySelector('#editArtworksDialog');
		this.dialog.openDialog();
		this._handleConfirm = async () => {
			const options = {
				artworkIds: selectedArtworks(this.state),
				publication: this.selectedChange.publication,
				ownership: this.selectedChange.ownership,
				series: this.selectedChange.series,
			};
			const result = await store.dispatch(massUpdateArtworks(options));
			if (result.meta.requestStatus === 'fulfilled') {
				this._refreshArtworkList();
				this.selectedChange = {
					publication: '',
					ownership: '',
					series: '',
				};
			}
			window.removeEventListener('confirm', this._handleConfirm);
		};
		window.addEventListener('confirm', this._handleConfirm);
	}

	editSelectedChange(field, value) {
		this.selectedChange[field] = value;
	}

	getSeriesOptions() {
		let seriesOpions = [];
		if (selectAllSerie(this.state).length > 0) {
			seriesOpions = selectAllSerie(this.state).map((serie) => ({
				id: serie.id,
				name: getValueForLanguage(serie.title, this.contentLanguage, true),
			}));
		}
		return [{id: '', name: ''}, ...seriesOpions];
	}

	selectedOnThisPage() {
		return this.artworks.reduce(
			(acc, artwork) => (selectedArtworks(this.state).includes(artwork.id) ? acc + 1 : acc),
			0
		);
	}

	someSelected() {
		return this.selectedOnThisPage() > 0;
	}

	allSelected() {
		return this.state.artwork.artworkPagination.amount === this.selectedOnThisPage();
	}

	isIndeterminate() {
		return this.someSelected() && !this.allSelected();
	}

	render() {
		// if al data is available, sort this.artworks according to manualSortOrder
		if (
			this.artworks &&
			this.artworks.length > 0 &&
			this.manualSortOrder &&
			this.manualSortOrder.length > 0
		) {
			this.artworks = this.manualSortOrder
				.map((id) => this.artworks.find((artwork) => artwork.id === id))
				.filter((artwork) => artwork); // filter not available artworks,i.e. deleted
		}

		return html`
			${!this.noFilters
				? html` <arc-artwork-list-filter class="artworkListFilter"></arc-artwork-list-filter>`
				: ''}

			<div class="list">
				${this.artworks.length > 0
					? html`
							<div class="listHeader nosort">
								<div class="selectAllCheckbox">
									${when(
										this.selectMultiple && this.permittedToEdit,
										() => html`
											<arc-checkbox
												class="formInput checkbox"
												title=""
												.noFieldTitle=${true}
												title=${msg('')}
												.value=${this.someSelected() ? 1 : 0}
												.showIcon=${false}
												.required=${false}
												.changeIndication=${false}
												.indeterminate=${this.isIndeterminate()}
												.tooltip="${false}"
												@value-change=${(e) => this.selectAllArtworks(e.detail.value)}
											>
											</arc-checkbox>
										`
									)}
								</div>
								<div>${msg('Artwork')}</div>
								<div>
									<arc-routerlink .route="artworks" .query=${this.getOrderQuery('inventoryNumber')}>
										${msg('Nr.')} ${this.getOrderDirectionIcon('inventoryNumber')}
									</arc-routerlink>
								</div>
								<div>${msg('Title')}</div>
								<div>${msg('Materials')}</div>
								<div>
									<arc-routerlink .route="artworks" .query=${this.getOrderQuery('artworkDate')}>
										${msg('Year')} ${this.getOrderDirectionIcon('artworkDate')}
									</arc-routerlink>
								</div>
								<div>${msg('Publication')}</div>
							</div>

							${repeat(
								this.artworks,
								(artwork) => artwork.id,
								(artwork, index) => html`
									<div class="canBeSorted">
										<arc-artwork-list-item
											.artwork=${artwork}
											.currentLanguage=${this.currentLanguage}
											.selectMultiple=${this.selectMultiple}
										></arc-artwork-list-item>
										${when(
											this.seriesId && this.artworks.length > 1,
											() => html` <div class="dragHandle" title="${msg('drag')}">${grab}</div> `
										)}
									</div>
								`
							)}

							<div class="artworkListFooter nosort">
								${when(
									this.selectMultiple && this.permittedToEdit,
									() => html`
										<arc-button
											@click=${this.onEditArtworks}
											.disabled=${selectedArtworks(this.state).length === 0}
											title="${msg('Edit Selected')} (${selectedArtworks(this.state).length})"
											type="secondary"
											.additionalInfo="${false}"
										>
										</arc-button>
									`
								)}
								<div class="paginationContainer">
									<arc-pagination .totalPages=${this.totalPages}></arc-pagination>
								</div>
							</div>
					  `
					: html` <p class="emptyMessage">${msg('No artworks found.')}</p> `}
			</div>

			<arc-request-dialog
				id="editArtworksDialog"
				title="${msg('Edit Artworks')}"
				message="${msg('Edit all selected Artworks')}"
			>
				<div class="editArtworksDialogContent">
					<arc-select
						class="formInput"
						.multiple=${false}
						.title="${msg('Publication')}"
						.edit=${true}
						.changeIndication=${false}
						.options=${[
							{id: '', name: msg('')},
							{id: 'draft', name: msg('Draft')},
							{id: 'private', name: msg('Private')},
							{id: 'public', name: msg('Publication permitted')},
						]}
						.value=${this.selectedChange.publication}
						@value-change=${(e) => this.editSelectedChange('publication', e.detail.value)}
					></arc-select>
					<arc-select
						class="formInput"
						.multiple=${false}
						.title="${msg('Ownership')}"
						.edit=${true}
						.changeIndication=${false}
						.options=${[
							{id: '', name: msg('')},
							{id: 'sold', name: msg('Sold')},
							{id: 'loaned', name: msg('Loaned')},
							{id: 'unsellable', name: msg('Unsellable')},
						]}
						.value=${this.selectedChange.ownership}
						@value-change=${(e) => this.editSelectedChange('ownership', e.detail.value)}
					></arc-select>
					<arc-select
						class="formInput"
						.multiple=${false}
						.title="${msg('Series')}"
						.edit=${true}
						.changeIndication=${false}
						.options=${this.getSeriesOptions()}
						.value=${this.selectedChange.series}
						@value-change=${(e) => this.editSelectedChange('series', e.detail.value)}
					></arc-select>
				</div>
			</arc-request-dialog>
		`;
	}
}
